/**
 * System Lizard - Interactive Patch Cable Router
 * Manages SVG virtual patch cables with physics slack curves and jack connections.
 */

export class PatchBay {
  constructor(svgContainer, options = {}) {
    this.svg = svgContainer;
    this.onConnect = options.onConnect || null;
    this.onDisconnect = options.onDisconnect || null;

    this.jacks = new Map(); // id -> { el, type ('in'|'out'), name }
    this.cables = [];       // Array of { id, fromId, toId, color, pathEl }

    this.activeDragCable = null;
    this.colors = ['#ff4757', '#ffa502', '#2ed573', '#1e90ff', '#e84393', '#70a1ff'];

    this.initOverlay();
  }

  initOverlay() {
    this.svg.setAttribute('width', '100%');
    this.svg.setAttribute('height', '100%');
    this.svg.style.position = 'absolute';
    this.svg.style.top = '0';
    this.svg.style.left = '0';
    this.svg.style.pointerEvents = 'none';
  }

  registerJack(id, element, type, name) {
    element.dataset.jackId = id;
    element.dataset.jackType = type;
    this.jacks.set(id, { id, el: element, type, name });

    element.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.handleJackPointerDown(id, e);
    });
  }

  getJackCenter(jackId) {
    const jack = this.jacks.get(jackId);
    if (!jack) return { x: 0, y: 0 };
    const rect = jack.el.getBoundingClientRect();
    const svgRect = this.svg.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2 - svgRect.left,
      y: rect.top + rect.height / 2 - svgRect.top
    };
  }

  handleJackPointerDown(jackId, event) {
    // If jack already has cable, remove it
    const existingIndex = this.cables.findIndex(c => c.fromId === jackId || c.toId === jackId);
    if (existingIndex !== -1) {
      const removed = this.cables.splice(existingIndex, 1)[0];
      removed.pathEl.remove();
      if (this.onDisconnect) this.onDisconnect(removed);
      return;
    }

    const startPos = this.getJackCenter(jackId);
    const color = this.colors[Math.floor(Math.random() * this.colors.length)];

    const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathEl.setAttribute('stroke', color);
    pathEl.setAttribute('stroke-width', '5');
    pathEl.setAttribute('stroke-linecap', 'round');
    pathEl.setAttribute('fill', 'none');
    pathEl.setAttribute('filter', 'drop-shadow(0px 8px 6px rgba(0,0,0,0.5))');
    this.svg.appendChild(pathEl);

    this.activeDragCable = {
      fromId: jackId,
      color,
      pathEl,
      startPos
    };

    const onMove = (e) => {
      if (!this.activeDragCable) return;
      const svgRect = this.svg.getBoundingClientRect();
      const currentPos = {
        x: e.clientX - svgRect.left,
        y: e.clientY - svgRect.top
      };
      this.drawBezierPath(pathEl, startPos, currentPos);
    };

    const onUp = (e) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);

      if (!this.activeDragCable) return;
      
      // Find element under pointer
      const targetEl = document.elementFromPoint(e.clientX, e.clientY);
      const targetJackId = targetEl?.dataset?.jackId || targetEl?.closest('[data-jack-id]')?.dataset?.jackId;

      if (targetJackId && targetJackId !== jackId) {
        const fromJack = this.jacks.get(jackId);
        const toJack = this.jacks.get(targetJackId);

        if (fromJack && toJack && fromJack.type !== toJack.type) {
          // Valid patch connection!
          const endPos = this.getJackCenter(targetJackId);
          this.drawBezierPath(pathEl, startPos, endPos);

          const newCable = {
            id: `${jackId}-${targetJackId}`,
            fromId: jackId,
            toId: targetJackId,
            color,
            pathEl
          };
          this.cables.push(newCable);

          if (this.onConnect) {
            this.onConnect(fromJack, toJack);
          }
          this.activeDragCable = null;
          return;
        }
      }

      // Invalid release, destroy draft cable
      pathEl.remove();
      this.activeDragCable = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  drawBezierPath(pathEl, p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Natural cable slack sag down
    const slack = Math.min(180, dist * 0.4 + 40);

    const cp1x = p1.x;
    const cp1y = p1.y + slack;
    const cp2x = p2.x;
    const cp2y = p2.y + slack;

    pathEl.setAttribute('d', `M ${p1.x} ${p1.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
  }

  updateAllCables() {
    for (const cable of this.cables) {
      const p1 = this.getJackCenter(cable.fromId);
      const p2 = this.getJackCenter(cable.toId);
      this.drawBezierPath(cable.pathEl, p1, p2);
    }
  }

  clearAllCables() {
    for (const cable of this.cables) {
      cable.pathEl.remove();
    }
    this.cables = [];
  }
}
